import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertCircle, TrendingUp, TrendingDown, FileText, Download, Upload, Archive, Trash2, FileDown, Sparkles, CheckCircle2, XCircle, ExternalLink, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function VoceCE({ label, value, indent = 0, bold = false, muted = false, big = false }) {
  return (
    <div className={`flex justify-between py-1.5 border-b border-slate-100 ${bold ? 'font-bold border-slate-300 bg-slate-50 px-2' : ''} ${big ? 'text-lg' : 'text-sm'}`} style={{ paddingLeft: `${indent * 12 + 4}px` }}>
      <span className={muted ? 'text-slate-500' : 'text-slate-800'}>{label}</span>
      <span className={`font-mono ${(Number(value) || 0) < 0 ? 'text-red-700' : ''}`}>€ {fmt(value)}</span>
    </div>
  );
}

export default function BilancioPage() {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [anniDisponibili, setAnniDisponibili] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storico, setStorico] = useState([]);
  const [uploadAnno, setUploadAnno] = useState(new Date().getFullYear() - 1);
  const [uploadNote, setUploadNote] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [riepiloghi, setRiepiloghi] = useState([]);
  const [rpForm, setRpForm] = useState({ mese: 1, salari_stipendi: '', oneri_sociali: '', tfr_maturato: '', altri_costi: '', note: '' });
  const [rpSaving, setRpSaving] = useState(false);
  const [rpAggregato, setRpAggregato] = useState(null);
  const [bustePagaDetail, setBustePagaDetail] = useState(null);
  const [importingPdf, setImportingPdf] = useState(false);
  const [importPdfFile, setImportPdfFile] = useState(null);
  const importPdfRef = useRef(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);

  useEffect(() => { fetchAnni(); fetchStorico(); }, []);
  useEffect(() => { if (anno) { fetchBilancio(anno); fetchRiepiloghi(anno); fetchAggregato(anno); fetchBustePagaDetail(anno); } }, [anno]);

  const fetchAnni = async () => {
    try {
      const r = await axios.get(`${API}/bilancio/anni-disponibili`, { withCredentials: true });
      const anni = r.data || [];
      const curr = new Date().getFullYear();
      const finale = anni.length ? anni : [curr];
      setAnniDisponibili(finale);
      if (!finale.includes(anno)) setAnno(finale[0]);
    } catch { setAnniDisponibili([new Date().getFullYear()]); }
  };

  const fetchBilancio = async (a) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bilancio?anno=${a}`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento bilancio');
    } finally { setLoading(false); }
  };

  const fetchStorico = async () => {
    try {
      const r = await axios.get(`${API}/bilancio/storico`, { withCredentials: true });
      setStorico(r.data || []);
    } catch { /* ignore */ }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Seleziona un file PDF'); return; }
    if (!uploadFile.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo PDF sono accettati'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('anno', String(uploadAnno));
      fd.append('note', uploadNote || '');
      fd.append('file', uploadFile);
      const r = await axios.post(`${API}/bilancio/storico`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStorico(prev => [r.data, ...prev]);
      toast.success(`Bilancio ${uploadAnno} archiviato`);
      setUploadFile(null);
      setUploadNote('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore upload PDF');
    } finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const r = await axios.get(`${API}/bilancio/storico/${doc.id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `bilancio_${doc.anno}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore download PDF');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare il bilancio ${doc.anno} "${doc.filename}"?`)) return;
    try {
      await axios.delete(`${API}/bilancio/storico/${doc.id}`, { withCredentials: true });
      setStorico(prev => prev.filter(x => x.id !== doc.id));
      toast.success('Bilancio archiviato eliminato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const fetchRiepiloghi = async (a) => {
    try {
      const r = await axios.get(`${API}/bilancio/costi-personale?anno=${a}`, { withCredentials: true });
      setRiepiloghi(r.data || []);
    } catch { setRiepiloghi([]); }
  };

  const fetchAggregato = async (a) => {
    try {
      const r = await axios.get(`${API}/bilancio/costi-personale/aggregato?anno=${a}`, { withCredentials: true });
      setRpAggregato(r.data);
    } catch { setRpAggregato(null); }
  };

  const fetchBustePagaDetail = async (a) => {
    try {
      const r = await axios.get(`${API}/bilancio/costi-personale/buste-paga-detail?anno=${a}`, { withCredentials: true });
      setBustePagaDetail(r.data);
    } catch { setBustePagaDetail(null); }
  };

  const saveRiepilogo = async (e) => {
    e.preventDefault();
    const num = (v) => parseFloat(String(v || '0').replace(',', '.')) || 0;
    setRpSaving(true);
    try {
      const body = {
        anno,
        mese: parseInt(rpForm.mese, 10),
        salari_stipendi: num(rpForm.salari_stipendi),
        oneri_sociali: num(rpForm.oneri_sociali),
        tfr_maturato: num(rpForm.tfr_maturato),
        altri_costi: num(rpForm.altri_costi),
        note: rpForm.note || '',
      };
      await axios.post(`${API}/bilancio/costi-personale`, body, { withCredentials: true });
      toast.success(`Riepilogo ${body.mese}/${anno} salvato`);
      setRpForm({ mese: 1, salari_stipendi: '', oneri_sociali: '', tfr_maturato: '', altri_costi: '', note: '' });
      await fetchRiepiloghi(anno);
      await fetchAggregato(anno);
      await fetchBustePagaDetail(anno);
      await fetchBilancio(anno);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio riepilogo');
    } finally { setRpSaving(false); }
  };

  const deleteRiepilogo = async (row) => {
    if (!window.confirm(`Eliminare il riepilogo ${row.mese}/${row.anno}?`)) return;
    try {
      await axios.delete(`${API}/bilancio/costi-personale/${row.id}`, { withCredentials: true });
      toast.success('Riepilogo eliminato');
      await fetchRiepiloghi(anno);
      await fetchAggregato(anno);
      await fetchBustePagaDetail(anno);
      await fetchBilancio(anno);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const editRiepilogo = (row) => {
    setRpForm({
      mese: row.mese,
      salari_stipendi: String(row.salari_stipendi || ''),
      oneri_sociali: String(row.oneri_sociali || ''),
      tfr_maturato: String(row.tfr_maturato || ''),
      altri_costi: String(row.altri_costi || ''),
      note: row.note || '',
    });
    // Scroll to top of form
    document.getElementById('riepilogo-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleImportPdf = async (e) => {
    e.preventDefault();
    if (!importPdfFile) { toast.error('Seleziona un PDF del riepilogo'); return; }
    if (!importPdfFile.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo PDF'); return; }
    setImportingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', importPdfFile);
      const r = await axios.post(`${API}/bilancio/costi-personale/import-pdf`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      // Prefill review dialog with extracted data
      const dati = r.data.dati || {};
      const periodo = dati.periodo || {};
      setReviewData({
        file_url: r.data.file_url,
        filename: r.data.filename_originale,
        anno: periodo.anno || anno,
        mese: periodo.mese || rpForm.mese,
        salari_stipendi: dati.totale_salari_stipendi ?? '',
        oneri_sociali: dati.totale_oneri_sociali ?? '',
        tfr_maturato: dati.totale_tfr_maturato ?? '',
        altri_costi: dati.totale_altri_costi ?? '',
        n_dipendenti: dati.n_dipendenti,
        totale_costo_azienda: dati.totale_costo_azienda,
        confidence: dati.confidence ?? 0,
        note_extra: dati.note_extra || '',
        commercialista_note: dati.commercialista_note || '',
        ragione_sociale: dati.ragione_sociale_azienda,
      });
      setReviewOpen(true);
      setImportPdfFile(null);
      if (importPdfRef.current) importPdfRef.current.value = '';
      toast.success(`PDF analizzato · Confidence ${((dati.confidence || 0) * 100).toFixed(0)}%`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore estrazione AI');
    } finally { setImportingPdf(false); }
  };

  const confirmImportedRiepilogo = async () => {
    if (!reviewData) return;
    const num = (v) => parseFloat(String(v || '0').replace(',', '.')) || 0;
    const anno_r = parseInt(reviewData.anno, 10);
    const mese_r = parseInt(reviewData.mese, 10);
    if (!anno_r || anno_r < 1990) { toast.error('Anno non valido'); return; }
    if (!mese_r || mese_r < 1 || mese_r > 12) { toast.error('Mese non valido (1-12)'); return; }
    try {
      const body = {
        anno: anno_r,
        mese: mese_r,
        salari_stipendi: num(reviewData.salari_stipendi),
        oneri_sociali: num(reviewData.oneri_sociali),
        tfr_maturato: num(reviewData.tfr_maturato),
        altri_costi: num(reviewData.altri_costi),
        note: `Importato da PDF (${reviewData.filename})${reviewData.note_extra ? ' · ' + reviewData.note_extra : ''}`.slice(0, 500),
      };
      await axios.post(`${API}/bilancio/costi-personale`, body, { withCredentials: true });
      toast.success(`Riepilogo ${mese_r}/${anno_r} importato dal PDF`);
      setReviewOpen(false);
      setReviewData(null);
      // Se l'anno importato è quello corrente, ricarica; altrimenti cambia anno
      if (anno_r === anno) {
        await fetchRiepiloghi(anno);
        await fetchAggregato(anno);
        await fetchBustePagaDetail(anno);
        await fetchBilancio(anno);
      } else {
        setAnno(anno_r);  // triggerà useEffect
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  const ce = data.conto_economico;
  const sp = data.stato_patrimoniale;
  const utile = ce.utile_perdita_esercizio;

  return (
    <div className="space-y-6" data-testid="bilancio-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Bilancio d&apos;Esercizio</h1>
          <p className="text-slate-500 mt-1">Conto Economico + Stato Patrimoniale ({data.riferimento_normativo})</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(anno)} onValueChange={v => setAnno(parseInt(v, 10))}>
            <SelectTrigger className="w-[130px]" data-testid="bilancio-anno-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {anniDisponibili.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => window.print()} data-testid="bilancio-print-btn"><Download className="h-4 w-4 mr-1.5" />Stampa</Button>
        </div>
      </div>

      {/* KPI riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-4"><div className="flex items-center gap-1 mb-1"><TrendingUp className="h-3.5 w-3.5 text-blue-600" /><p className="text-[10px] text-blue-600 uppercase font-medium">Valore Produzione</p></div><p className="text-2xl font-bold text-blue-700" data-testid="valore-produzione">{fmt(ce.A_valore_produzione.totale)}</p></CardContent></Card>
        <Card className="border-red-200 bg-red-50/50"><CardContent className="p-4"><div className="flex items-center gap-1 mb-1"><TrendingDown className="h-3.5 w-3.5 text-red-600" /><p className="text-[10px] text-red-600 uppercase font-medium">Costi Produzione</p></div><p className="text-2xl font-bold text-red-700" data-testid="costi-produzione">{fmt(ce.B_costi_produzione.totale)}</p></CardContent></Card>
        <Card className={`${utile >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`}><CardContent className="p-4"><p className="text-[10px] text-slate-600 uppercase font-medium">Utile / Perdita</p><p className={`text-2xl font-bold ${utile >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="utile-esercizio">{fmt(utile)}</p></CardContent></Card>
        <Card className="border-slate-200 bg-slate-50/50"><CardContent className="p-4"><p className="text-[10px] text-slate-600 uppercase font-medium">IVA Saldo</p><p className={`text-2xl font-bold ${data.iva.iva_saldo > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="iva-saldo">{fmt(data.iva.iva_saldo)}</p><p className="text-[10px] text-slate-500 mt-0.5">{data.iva.iva_saldo > 0 ? 'da versare' : 'a credito'}</p></CardContent></Card>
      </div>

      {(data.avvertenze || []).filter(Boolean).length > 0 && (
        <Card className="border-amber-300 bg-amber-50/30">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold mb-1">Avvertenze professionali</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {(data.avvertenze || []).filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="ce">
        <TabsList>
          <TabsTrigger value="ce" data-testid="tab-ce"><FileText className="h-4 w-4 mr-1.5" />Conto Economico</TabsTrigger>
          <TabsTrigger value="sp" data-testid="tab-sp"><FileText className="h-4 w-4 mr-1.5" />Stato Patrimoniale</TabsTrigger>
          <TabsTrigger value="dettagli" data-testid="tab-dettagli">Dettagli</TabsTrigger>
          <TabsTrigger value="personale" data-testid="tab-personale">Costi Personale</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico"><Archive className="h-4 w-4 mr-1.5" />Archivio Storico</TabsTrigger>
        </TabsList>

        {/* Conto Economico */}
        <TabsContent value="ce">
          <Card>
            <CardHeader><CardTitle className="text-lg">Conto Economico {anno} (art. 2425 CC)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="px-4 pb-4">
                <VoceCE label="A) Valore della produzione" value={ce.A_valore_produzione.totale} bold />
                <VoceCE label="Ricavi delle vendite e delle prestazioni" value={ce.A_valore_produzione.ricavi_vendite_prestazioni} indent={1} />

                <VoceCE label="B) Costi della produzione" value={-ce.B_costi_produzione.totale} bold />
                <VoceCE label="6) Materie prime, sussidiarie, di consumo" value={-ce.B_costi_produzione.B6_materie_prime} indent={1} muted />
                <VoceCE label="7) Per servizi (fatture ricevute + compensi soci)" value={-ce.B_costi_produzione.B7_servizi_e_godimento_beni_terzi} indent={1} />
                <VoceCE label="9) Per il personale" value={-((ce.B_costi_produzione.B9_costi_personale?.totale) ?? 0)} indent={1} bold />
                <VoceCE label="9.a) Salari e stipendi" value={-((ce.B_costi_produzione.B9_costi_personale?.b9a_salari_stipendi) ?? 0)} indent={2} />
                <VoceCE label="9.b) Oneri sociali (INPS/INAIL azienda)" value={-((ce.B_costi_produzione.B9_costi_personale?.b9b_oneri_sociali) ?? 0)} indent={2} />
                <VoceCE label="9.c) TFR maturato nell'anno" value={-((ce.B_costi_produzione.B9_costi_personale?.b9c_tfr) ?? 0)} indent={2} />
                <VoceCE label="9.d) Trattamento quiescenza" value={-((ce.B_costi_produzione.B9_costi_personale?.b9d_trattamento_quiescenza) ?? 0)} indent={2} muted />
                <VoceCE label="9.e) Altri costi (trasferte, welfare)" value={-((ce.B_costi_produzione.B9_costi_personale?.b9e_altri_costi) ?? 0)} indent={2} />
                <div className="text-[10px] text-slate-400 italic ml-6 mb-1">Fonte: {ce.B_costi_produzione.B9_costi_personale?.fonte || 'nessuna'}{ce.B_costi_produzione.B9_costi_personale?.modalita ? ` · Modalità: ${ce.B_costi_produzione.B9_costi_personale.modalita}` : ''}</div>
                <VoceCE label="10) Ammortamenti e svalutazioni" value={-ce.B_costi_produzione.B10_ammortamenti_svalutazioni} indent={1} muted />
                <VoceCE label="14) Oneri diversi (contributi F24)" value={-ce.B_costi_produzione.B14_oneri_diversi_gestione} indent={1} />

                <VoceCE label="Differenza A − B" value={ce.differenza_A_meno_B} bold big />
                <VoceCE label="C) Proventi e oneri finanziari" value={ce.C_proventi_oneri_finanziari} muted />
                <VoceCE label="D) Rettifiche valore attività finanziarie" value={ce.D_rettifiche_valore_attivita_finanziarie} muted />
                <VoceCE label="Risultato ante imposte" value={ce.risultato_ante_imposte} bold />
                <VoceCE label="Imposte sul reddito (F24 imposte)" value={-ce.imposte_esercizio} />
                <VoceCE label="Utile / (Perdita) d'esercizio" value={ce.utile_perdita_esercizio} bold big />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stato Patrimoniale */}
        <TabsContent value="sp">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-lg">Attivo</CardTitle></CardHeader>
              <CardContent className="p-4">
                <VoceCE label="B) Immobilizzazioni" value={sp.attivo.B_immobilizzazioni.totale} bold />
                <p className="text-[10px] text-slate-400 italic ml-3 mb-1">{sp.attivo.B_immobilizzazioni.note}</p>
                <VoceCE label="C) Attivo circolante" value={sp.attivo.C_attivo_circolante.totale} bold />
                <VoceCE label="C.II — Crediti verso clienti" value={sp.attivo.C_attivo_circolante.CII_crediti_verso_clienti} indent={1} />
                <VoceCE label="C.II — Crediti tributari (IVA)" value={sp.attivo.C_attivo_circolante.CII_crediti_tributari} indent={1} />
                <VoceCE label="C.IV — Disponibilità liquide" value={sp.attivo.C_attivo_circolante.CIV_disponibilita_liquide} indent={1} muted />
                <VoceCE label="D) Ratei e risconti attivi" value={sp.attivo.D_ratei_risconti_attivi} muted />
                <VoceCE label="TOTALE ATTIVO" value={sp.attivo.totale_attivo} bold big />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Passivo</CardTitle></CardHeader>
              <CardContent className="p-4">
                <VoceCE label="A) Patrimonio netto" value={sp.passivo.A_patrimonio_netto.totale} bold />
                <VoceCE label="A.I — Capitale sociale" value={sp.passivo.A_patrimonio_netto.AI_capitale_sociale} indent={1} muted />
                <VoceCE label="A.VIII — Utili portati a nuovo" value={sp.passivo.A_patrimonio_netto.AVIII_utili_perditi_portati_a_nuovo} indent={1} muted />
                <VoceCE label="A.IX — Utile/perdita d'esercizio" value={sp.passivo.A_patrimonio_netto.AIX_utile_perdita_esercizio} indent={1} />
                <VoceCE label="C) TFR" value={sp.passivo.C_trattamento_fine_rapporto} muted />
                <VoceCE label="D) Debiti" value={sp.passivo.D_debiti.totale} bold />
                <VoceCE label="D.7 — Debiti verso fornitori" value={sp.passivo.D_debiti.D7_debiti_verso_fornitori} indent={1} />
                <VoceCE label="D.12 — Debiti tributari" value={sp.passivo.D_debiti.D12_debiti_tributari} indent={1} />
                <VoceCE label="D.13 — Debiti verso previdenza" value={sp.passivo.D_debiti.D13_debiti_verso_previdenza} indent={1} />
                <VoceCE label="D.14 — Altri debiti (soci)" value={sp.passivo.D_debiti.D14_altri_debiti} indent={1} />
                <VoceCE label="E) Ratei e risconti passivi" value={sp.passivo.E_ratei_risconti_passivi} muted />
                <VoceCE label="TOTALE PASSIVO" value={sp.passivo.totale_passivo} bold big />
              </CardContent>
            </Card>
          </div>
          <Card className="mt-3 border-slate-200 bg-slate-50/30">
            <CardContent className="p-3 text-xs text-slate-600">
              <p><strong>Nota di quadratura:</strong> Attivo ({fmt(sp.attivo.totale_attivo)}) vs Passivo ({fmt(sp.passivo.totale_passivo)}). Se non tornano, mancano voci contabili manuali (immobilizzazioni, TFR, capitale, disponibilità liquide).</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dettagli */}
        <TabsContent value="dettagli">
          <div className="grid md:grid-cols-2 gap-4">
            <Card><CardHeader><CardTitle className="text-sm">Fatture emesse (ricavi)</CardTitle></CardHeader><CardContent className="p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Ricavi imponibile</span><span className="font-mono">{fmt(data.dettagli?.ricavi?.ricavi_imponibile)}</span></div>
              <div className="flex justify-between"><span>IVA a debito</span><span className="font-mono">{fmt(data.dettagli?.ricavi?.ricavi_iva_debito)}</span></div>
              <div className="flex justify-between text-slate-500"><span>Note credito emesse</span><span className="font-mono">-{fmt(data.dettagli?.ricavi?.note_credito_emesse)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Effettivamente incassato</span><span className="font-mono">{fmt(data.dettagli?.ricavi?.ricavi_incassati)}</span></div>
              <div className="flex justify-between text-slate-400 text-xs"><span>N. fatture</span><span>{data.dettagli?.ricavi?.n_fatture}</span></div>
            </CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm">Fatture ricevute (costi)</CardTitle></CardHeader><CardContent className="p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Costi imponibile</span><span className="font-mono">{fmt(data.dettagli?.costi_fatture?.costi_imponibile)}</span></div>
              <div className="flex justify-between"><span>IVA a credito</span><span className="font-mono">{fmt(data.dettagli?.costi_fatture?.costi_iva_credito)}</span></div>
              <div className="flex justify-between text-slate-500"><span>Note credito ricevute</span><span className="font-mono">-{fmt(data.dettagli?.costi_fatture?.note_credito_ricevute)}</span></div>
              <div className="flex justify-between text-red-700"><span>Effettivamente pagato</span><span className="font-mono">{fmt(data.dettagli?.costi_fatture?.costi_pagati)}</span></div>
              <div className="flex justify-between text-slate-400 text-xs"><span>N. fatture</span><span>{data.dettagli?.costi_fatture?.n_fatture}</span></div>
            </CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm">Compensi Soci</CardTitle></CardHeader><CardContent className="p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Lordo</span><span className="font-mono">{fmt(data.dettagli?.compensi_soci?.compensi_soci_lordo)}</span></div>
              <div className="flex justify-between"><span>Netto</span><span className="font-mono">{fmt(data.dettagli?.compensi_soci?.compensi_soci_netto)}</span></div>
              <div className="flex justify-between"><span>Contributi INPS</span><span className="font-mono">{fmt(data.dettagli?.compensi_soci?.contributi_soci_inps)}</span></div>
              <div className="flex justify-between text-amber-700"><span>Da pagare</span><span className="font-mono">{fmt(data.dettagli?.compensi_soci?.compensi_soci_da_pagare)}</span></div>
            </CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm">Costi del Personale (B.9)</CardTitle></CardHeader><CardContent className="p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>B.9.a Salari lordi</span><span className="font-mono">{fmt(data.dettagli?.costi_personale?.b9a_salari_stipendi)}</span></div>
              <div className="flex justify-between"><span>B.9.b Oneri INPS azienda</span><span className="font-mono">{fmt(data.dettagli?.costi_personale?.b9b_oneri_sociali)}</span></div>
              <div className="flex justify-between"><span>B.9.c TFR maturato</span><span className="font-mono">{fmt(data.dettagli?.costi_personale?.b9c_tfr)}</span></div>
              <div className="flex justify-between"><span>B.9.e Trasferte/Altri</span><span className="font-mono">{fmt(data.dettagli?.costi_personale?.b9e_altri_costi)}</span></div>
              <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Totale B.9</span><span className="font-mono">{fmt(data.dettagli?.costi_personale?.totale)}</span></div>
              <div className="flex justify-between text-slate-400 text-xs pt-1"><span>Fonte</span><span>{data.dettagli?.costi_personale?.fonte || 'nessuna'}</span></div>
            </CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm">F24 Versati</CardTitle></CardHeader><CardContent className="p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Totale F24</span><span className="font-mono">{fmt(data.dettagli?.f24?.f24_totale)}</span></div>
              <div className="flex justify-between"><span>Imposte</span><span className="font-mono">{fmt(data.dettagli?.f24?.f24_imposte)}</span></div>
              <div className="flex justify-between"><span>Contributi</span><span className="font-mono">{fmt(data.dettagli?.f24?.f24_contributi)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Versati</span><span className="font-mono">{fmt(data.dettagli?.f24?.f24_versati)}</span></div>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Costi Personale (Ibrido: buste paga AI + riepilogo manuale) */}
        <TabsContent value="personale">
          <div className="space-y-4">
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="text-base">Aggregazione anno {anno}</CardTitle>
                <p className="text-xs text-slate-600 mt-1">
                  Il bilancio usa la logica <strong>ibrida</strong>: per ogni mese, se esiste un riepilogo manuale ha priorità sulle buste paga AI. Se non c&apos;è nessuno dei due, viene stimato dai rapportini (solo B.9.a).
                </p>
              </CardHeader>
              <CardContent>
                {rpAggregato ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><p className="text-[10px] text-slate-500 uppercase">B.9.a Salari</p><p className="text-lg font-bold">€ {fmt(rpAggregato.b9a_salari_stipendi)}</p></div>
                    <div><p className="text-[10px] text-slate-500 uppercase">B.9.b Oneri Sociali</p><p className="text-lg font-bold">€ {fmt(rpAggregato.b9b_oneri_sociali)}</p></div>
                    <div><p className="text-[10px] text-slate-500 uppercase">B.9.c TFR</p><p className="text-lg font-bold">€ {fmt(rpAggregato.b9c_tfr)}</p></div>
                    <div><p className="text-[10px] text-slate-500 uppercase">B.9.e Altri</p><p className="text-lg font-bold">€ {fmt(rpAggregato.b9e_altri_costi)}</p></div>
                    <div className="col-span-2 md:col-span-4 border-t pt-2 mt-1">
                      <p className="text-xs text-slate-600">Totale B.9 nel Conto Economico: <span className="text-lg font-bold text-slate-900">€ {fmt(rpAggregato.totale)}</span></p>
                      <p className="text-[10px] text-slate-500 mt-1"><strong>Fonte:</strong> {rpAggregato.fonte} — <strong>Modalità:</strong> {rpAggregato.modalita}</p>
                      {rpAggregato.avviso && <p className="text-[10px] text-amber-700 mt-1"><AlertCircle className="h-3 w-3 inline mr-1" />{rpAggregato.avviso}</p>}
                    </div>
                  </div>
                ) : <p className="text-sm text-slate-500">Caricamento…</p>}
              </CardContent>
            </Card>

            {/* Buste Paga AI incluse nel bilancio */}
            <Card className="border-emerald-200 bg-emerald-50/20">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-600" />
                  Buste paga AI nel bilancio {anno}
                </CardTitle>
                <p className="text-xs text-slate-600 mt-1">
                  Le buste paga PDF caricate via <a href="/admin/buste-paga-import" className="text-blue-600 underline">Import Buste Paga (AI)</a> vengono <strong>aggregate automaticamente</strong> nel Conto Economico B.9. Nessun re-inserimento manuale necessario.
                </p>
              </CardHeader>
              <CardContent>
                {!bustePagaDetail ? (
                  <p className="text-sm text-slate-500">Caricamento…</p>
                ) : (
                  <div className="space-y-3">
                    {/* Incluse */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        <p className="text-sm font-semibold text-emerald-800">Incluse in B.9 ({bustePagaDetail.incluse.length})</p>
                        {bustePagaDetail.incluse.length > 0 && (
                          <Badge variant="outline" className="ml-auto text-xs">
                            Lordo € {fmt(bustePagaDetail.totali_incluse.b9a_salari_stipendi)} · INPS az. € {fmt(bustePagaDetail.totali_incluse.b9b_oneri_sociali)} · TFR € {fmt(bustePagaDetail.totali_incluse.b9c_tfr)}
                          </Badge>
                        )}
                      </div>
                      {bustePagaDetail.incluse.length === 0 ? (
                        <div className="p-3 bg-white rounded border border-slate-200 text-sm text-slate-600">
                          <p>Nessuna busta paga PDF importata per l&apos;anno {anno}. Per popolare automaticamente il costo del personale nel bilancio:</p>
                          <a href="/admin/buste-paga-import" className="mt-2 inline-flex items-center gap-1 text-blue-600 underline text-sm">
                            <ExternalLink className="h-3.5 w-3.5" /> Vai a Import Buste Paga (AI)
                          </a>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded border border-emerald-100">
                          <table className="w-full text-xs">
                            <thead className="bg-emerald-50 text-slate-700">
                              <tr>
                                <th className="text-left px-2 py-1.5">Dipendente</th>
                                <th className="text-center px-2 py-1.5">Periodo</th>
                                <th className="text-right px-2 py-1.5">Lordo</th>
                                <th className="text-right px-2 py-1.5">INPS az.</th>
                                <th className="text-right px-2 py-1.5">TFR</th>
                                <th className="text-right px-2 py-1.5">Trasferte</th>
                                <th className="px-2 py-1.5"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {bustePagaDetail.incluse.map(b => (
                                <tr key={b.id} className="border-t border-emerald-50 hover:bg-emerald-50/40" data-testid={`bp-incluse-${b.id}`}>
                                  <td className="px-2 py-1.5 font-medium">{b.nome_dipendente || '—'}</td>
                                  <td className="px-2 py-1.5 text-center">{String(b.periodo.mese).padStart(2,'0')}/{b.periodo.anno}</td>
                                  <td className="px-2 py-1.5 text-right font-mono">{fmt(b.importo_lordo)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono">{fmt(b.contributi_inps_azienda)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono">{fmt(b.tfr_maturato_periodo)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono">{fmt(b.trasferte_e_diarie)}</td>
                                  <td className="px-2 py-1.5 text-right">
                                    {b.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${b.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">PDF</a>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Sovrascritte da riepilogo manuale */}
                    {bustePagaDetail.escluse_mese_manuale.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          <p className="text-sm font-semibold text-amber-800">Sovrascritte dal riepilogo manuale ({bustePagaDetail.escluse_mese_manuale.length})</p>
                        </div>
                        <div className="text-xs text-slate-600 space-y-1 pl-6">
                          {bustePagaDetail.escluse_mese_manuale.map(b => (
                            <p key={b.id}>· <strong>{b.nome_dipendente}</strong> ({String(b.periodo.mese).padStart(2,'0')}/{b.periodo.anno}) — il mese è coperto dal riepilogo manuale che ha priorità</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Scartate (periodo mancante) */}
                    {bustePagaDetail.scartate_periodo_mancante.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <p className="text-sm font-semibold text-red-800">Scartate — periodo non riconosciuto ({bustePagaDetail.scartate_periodo_mancante.length})</p>
                        </div>
                        <div className="text-xs text-slate-600 space-y-1 pl-6">
                          {bustePagaDetail.scartate_periodo_mancante.map(b => (
                            <p key={b.id}>· <strong>{b.nome_dipendente || 'Senza nome'}</strong> — {b.motivo_scarto}. Modifica la busta impostando anno/mese o data pagamento.</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-purple-600" />
                  Importa da PDF (AI)
                </CardTitle>
                <p className="text-xs text-slate-600 mt-1">
                  Carica il PDF del <strong>riepilogo mensile</strong> inviato dal commercialista (LUL, prospetto costo aziendale, riassunto paghe). L&apos;AI estrae automaticamente salari, oneri INPS, TFR e altri costi. Poi conferma i valori nel dialog e li salva. Max 20 MB.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleImportPdf} className="grid md:grid-cols-3 gap-3 items-end" data-testid="riepilogo-import-form">
                  <div className="md:col-span-2">
                    <Label htmlFor="rp-import-file" className="text-xs">File PDF riepilogo</Label>
                    <Input
                      id="rp-import-file"
                      type="file"
                      accept=".pdf,application/pdf"
                      ref={importPdfRef}
                      onChange={e => setImportPdfFile(e.target.files?.[0] || null)}
                      data-testid="rp-import-file"
                    />
                  </div>
                  <div>
                    <Button type="submit" disabled={importingPdf || !importPdfFile} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="rp-import-submit">
                      <Wand2 className="h-4 w-4 mr-1.5" />
                      {importingPdf ? 'Estrazione AI in corso…' : 'Estrai con AI'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Aggiungi / modifica riepilogo mensile</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Usa questi campi per inserire manualmente il totale del mese fornito dal commercialista (es. cedolino aggregato). Sovrascrive le buste paga AI per lo stesso mese.</p>
              </CardHeader>
              <CardContent>
                <form id="riepilogo-form" onSubmit={saveRiepilogo} className="grid md:grid-cols-6 gap-3" data-testid="riepilogo-form">
                  <div className="md:col-span-1">
                    <Label htmlFor="rp-mese" className="text-xs">Mese</Label>
                    <Select value={String(rpForm.mese)} onValueChange={v => setRpForm(p => ({ ...p, mese: parseInt(v, 10) }))}>
                      <SelectTrigger data-testid="rp-mese"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                          <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="rp-salari" className="text-xs">Salari lordi (B.9.a) €</Label>
                    <Input id="rp-salari" type="text" inputMode="decimal" placeholder="0,00"
                      value={rpForm.salari_stipendi} onChange={e => setRpForm(p => ({ ...p, salari_stipendi: e.target.value }))} data-testid="rp-salari" />
                  </div>
                  <div>
                    <Label htmlFor="rp-oneri" className="text-xs">Oneri sociali (B.9.b) €</Label>
                    <Input id="rp-oneri" type="text" inputMode="decimal" placeholder="0,00"
                      value={rpForm.oneri_sociali} onChange={e => setRpForm(p => ({ ...p, oneri_sociali: e.target.value }))} data-testid="rp-oneri" />
                  </div>
                  <div>
                    <Label htmlFor="rp-tfr" className="text-xs">TFR (B.9.c) €</Label>
                    <Input id="rp-tfr" type="text" inputMode="decimal" placeholder="0,00"
                      value={rpForm.tfr_maturato} onChange={e => setRpForm(p => ({ ...p, tfr_maturato: e.target.value }))} data-testid="rp-tfr" />
                  </div>
                  <div>
                    <Label htmlFor="rp-altri" className="text-xs">Altri (B.9.e) €</Label>
                    <Input id="rp-altri" type="text" inputMode="decimal" placeholder="0,00"
                      value={rpForm.altri_costi} onChange={e => setRpForm(p => ({ ...p, altri_costi: e.target.value }))} data-testid="rp-altri" />
                  </div>
                  <div className="md:col-span-6">
                    <Label htmlFor="rp-note" className="text-xs">Note (opzionale)</Label>
                    <Input id="rp-note" placeholder="Es. Cedolino aggregato commercialista"
                      value={rpForm.note} onChange={e => setRpForm(p => ({ ...p, note: e.target.value }))} data-testid="rp-note" />
                  </div>
                  <div className="md:col-span-6 flex justify-end">
                    <Button type="submit" disabled={rpSaving} data-testid="rp-save">
                      {rpSaving ? 'Salvo…' : 'Salva riepilogo'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Riepiloghi mensili {anno} ({riepiloghi.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {riepiloghi.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Nessun riepilogo manuale per {anno}. Le buste paga AI verranno usate se disponibili.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-600">
                        <tr>
                          <th className="text-left py-2 px-3">Mese</th>
                          <th className="text-right py-2 px-3">Salari</th>
                          <th className="text-right py-2 px-3">Oneri INPS</th>
                          <th className="text-right py-2 px-3">TFR</th>
                          <th className="text-right py-2 px-3">Altri</th>
                          <th className="text-right py-2 px-3 font-bold">Totale</th>
                          <th className="text-left py-2 px-3">Note</th>
                          <th className="text-right py-2 px-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {riepiloghi.map(row => {
                          const tot = (row.salari_stipendi || 0) + (row.oneri_sociali || 0) + (row.tfr_maturato || 0) + (row.altri_costi || 0);
                          const mesi = ['','Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
                          return (
                            <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-3 font-medium">{mesi[row.mese]} {row.anno}</td>
                              <td className="py-2 px-3 text-right font-mono">{fmt(row.salari_stipendi)}</td>
                              <td className="py-2 px-3 text-right font-mono">{fmt(row.oneri_sociali)}</td>
                              <td className="py-2 px-3 text-right font-mono">{fmt(row.tfr_maturato)}</td>
                              <td className="py-2 px-3 text-right font-mono">{fmt(row.altri_costi)}</td>
                              <td className="py-2 px-3 text-right font-mono font-bold">{fmt(tot)}</td>
                              <td className="py-2 px-3 text-xs text-slate-500 truncate max-w-[200px]" title={row.note}>{row.note || '—'}</td>
                              <td className="py-2 px-3 text-right">
                                <Button size="sm" variant="ghost" onClick={() => editRiepilogo(row)} data-testid={`rp-edit-${row.mese}`}>Modifica</Button>
                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800" onClick={() => deleteRiepilogo(row)} data-testid={`rp-del-${row.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Archivio Storico Bilanci PDF */}
        <TabsContent value="storico">
          <div className="space-y-4">
            <Card className="border-indigo-200 bg-indigo-50/30">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Upload className="h-4 w-4 text-indigo-600" />Carica un bilancio precedente (PDF)</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Archivia il bilancio d&apos;esercizio depositato in Camera di Commercio o preparato dal commercialista. Utile per confrontare gli anni passati. Max 30 MB.</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpload} className="grid md:grid-cols-4 gap-3 items-end" data-testid="storico-upload-form">
                  <div>
                    <Label htmlFor="upload-anno" className="text-xs">Anno riferimento</Label>
                    <Input
                      id="upload-anno"
                      type="number"
                      min={1990}
                      max={new Date().getFullYear()}
                      value={uploadAnno}
                      onChange={e => setUploadAnno(parseInt(e.target.value || '0', 10))}
                      data-testid="storico-upload-anno"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="upload-note" className="text-xs">Note (opzionale)</Label>
                    <Input
                      id="upload-note"
                      placeholder="Es. Bilancio depositato CCIAA — approvato assemblea 20/04"
                      value={uploadNote}
                      onChange={e => setUploadNote(e.target.value)}
                      data-testid="storico-upload-note"
                    />
                  </div>
                  <div>
                    <Label htmlFor="upload-file" className="text-xs">File PDF</Label>
                    <Input
                      id="upload-file"
                      type="file"
                      accept=".pdf,application/pdf"
                      ref={fileInputRef}
                      onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      data-testid="storico-upload-file"
                    />
                  </div>
                  <div className="md:col-span-4 flex justify-end">
                    <Button type="submit" disabled={uploading || !uploadFile} data-testid="storico-upload-submit">
                      {uploading ? 'Caricamento…' : (<><Upload className="h-4 w-4 mr-1.5" />Archivia bilancio</>)}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Archive className="h-4 w-4 text-slate-600" />Bilanci archiviati ({storico.length})</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Conservazione obbligatoria art. 2220 CC (10 anni) — art. 2478-bis CC (bilanci depositati).</p>
              </CardHeader>
              <CardContent className="p-0">
                {storico.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm">
                    <Archive className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    Nessun bilancio archiviato. Carica il primo PDF sopra.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {storico.map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`storico-row-${doc.anno}`}>
                        <div className="flex-shrink-0 w-16 text-center">
                          <Badge variant="secondary" className="text-base font-bold px-2 py-0.5">{doc.anno}</Badge>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5 text-red-600" />
                            {doc.filename}
                          </p>
                          {doc.note && <p className="text-xs text-slate-500 truncate">{doc.note}</p>}
                          <p className="text-[10px] text-slate-400">
                            Caricato il {doc.upload_date ? new Date(doc.upload_date).toLocaleString('it-IT') : '—'} · {(doc.size_bytes / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleDownload(doc)} data-testid={`storico-download-${doc.anno}`}>
                            <FileDown className="h-4 w-4 mr-1" />Scarica
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800 hover:bg-red-50" onClick={() => handleDelete(doc)} data-testid={`storico-delete-${doc.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog review riepilogo estratto da PDF via AI */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-purple-600" />
              Conferma dati estratti dal PDF
            </DialogTitle>
          </DialogHeader>
          {reviewData && (
            <div className="space-y-3">
              <div className="text-xs bg-purple-50 border border-purple-200 rounded p-2">
                <p><strong>File:</strong> {reviewData.filename}</p>
                {reviewData.ragione_sociale && <p><strong>Azienda:</strong> {reviewData.ragione_sociale}</p>}
                {reviewData.n_dipendenti != null && <p><strong>Dipendenti nel prospetto:</strong> {reviewData.n_dipendenti}</p>}
                <p><strong>Confidence AI:</strong> {((reviewData.confidence || 0) * 100).toFixed(0)}% {reviewData.confidence < 0.7 && <span className="text-amber-700">— Verifica attentamente i valori</span>}</p>
                {reviewData.note_extra && <p className="italic text-slate-600">Note AI: {reviewData.note_extra}</p>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Anno</Label>
                  <Input type="number" value={reviewData.anno || ''} onChange={e => setReviewData(p => ({...p, anno: parseInt(e.target.value, 10)}))} data-testid="review-anno" />
                </div>
                <div>
                  <Label className="text-xs">Mese</Label>
                  <Select value={String(reviewData.mese || 1)} onValueChange={v => setReviewData(p => ({...p, mese: parseInt(v, 10)}))}>
                    <SelectTrigger data-testid="review-mese"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                        <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Salari lordi (B.9.a) €</Label>
                  <Input type="text" value={reviewData.salari_stipendi ?? ''} onChange={e => setReviewData(p => ({...p, salari_stipendi: e.target.value}))} data-testid="review-salari" />
                </div>
                <div>
                  <Label className="text-xs">Oneri sociali INPS/INAIL azienda (B.9.b) €</Label>
                  <Input type="text" value={reviewData.oneri_sociali ?? ''} onChange={e => setReviewData(p => ({...p, oneri_sociali: e.target.value}))} data-testid="review-oneri" />
                </div>
                <div>
                  <Label className="text-xs">TFR maturato (B.9.c) €</Label>
                  <Input type="text" value={reviewData.tfr_maturato ?? ''} onChange={e => setReviewData(p => ({...p, tfr_maturato: e.target.value}))} data-testid="review-tfr" />
                </div>
                <div>
                  <Label className="text-xs">Altri costi (B.9.e) €</Label>
                  <Input type="text" value={reviewData.altri_costi ?? ''} onChange={e => setReviewData(p => ({...p, altri_costi: e.target.value}))} data-testid="review-altri" />
                </div>
              </div>
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
                <p className="font-semibold">Totale calcolato:</p>
                <p className="font-mono">
                  € {fmt((parseFloat(reviewData.salari_stipendi) || 0) + (parseFloat(reviewData.oneri_sociali) || 0) + (parseFloat(reviewData.tfr_maturato) || 0) + (parseFloat(reviewData.altri_costi) || 0))}
                </p>
                {reviewData.totale_costo_azienda && (
                  <p className="text-slate-500 italic">Totale costo azienda estratto dal PDF: € {fmt(reviewData.totale_costo_azienda)}</p>
                )}
              </div>
              {reviewData.file_url && (
                <p className="text-xs"><a href={`${process.env.REACT_APP_BACKEND_URL}${reviewData.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Apri PDF originale</a></p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} data-testid="review-cancel">Annulla</Button>
            <Button onClick={confirmImportedRiepilogo} className="bg-purple-600 hover:bg-purple-700" data-testid="review-confirm">Conferma e salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
